import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { apiGet } from "../../api/client";
import { useAuth } from "../../auth/AuthContext";
import type { CurriculumSummary } from "../../types";

/** "Grade 12" → "GR 12", "NSC Exam Prep" → "NSC". The paper code a learner
 *  would read off the top of a question paper. */
function paperCode(gradeLabel: string): string {
  const m = gradeLabel.match(/Grade\s*(\d+)/i);
  if (m) return `GR ${m[1]}`;
  if (/nsc/i.test(gradeLabel)) return "NSC";
  return "—";
}

export function CurriculaPage() {
  // An admin is not enrolled in anything; the API hands them the whole
  // catalogue so they can look at the material. Calling that "my subjects"
  // told them they were studying seventeen courses.
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const { data, isLoading } = useQuery({
    queryKey: ["curricula"],
    queryFn: () => apiGet<CurriculumSummary[]>("/api/curricula"),
  });

  if (isLoading) return <p className="text-muted">Loading your subjects...</p>;

  // Learners only receive the subjects they're enrolled in, so an empty list
  // means nothing has been assigned yet — not that the catalogue is empty.
  if (!data?.length) {
    return (
      <div className="subjects">
        <header className="subjects-head">
          <h1>{isAdmin ? "All subjects" : "My subjects"}</h1>
        </header>
        {/* Enrolment is the learner's own to make, so the empty state is an
            action rather than an instruction to wait for someone. */}
        <div className="subjects-empty">
          <p className="subjects-empty-title">No subjects yet</p>
          <p className="subjects-empty-body">
            Pick the subjects you want to study — you can start straight away,
            and add or remove them whenever you like.
          </p>
          <Link to="/browse" className="btn btn-primary">
            Add a subject
          </Link>
        </div>
      </div>
    );
  }

  // Group by subject
  const grouped: Record<string, CurriculumSummary[]> = {};
  for (const c of data) {
    const key = c.subject || "Other";
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(c);
  }

  // Sort: Mathematics first, then Maths Lit, Accounting, English, then others
  const order: Record<string, number> = {
    "Mathematical Literacy": 0,
    Mathematics: 1,
    Accounting: 2,
    "English Home Language": 3,
    "English First Additional Language": 4,
    Programming: 5,
  };
  const subjects = Object.keys(grouped).sort(
    (a, b) => (order[a] ?? 99) - (order[b] ?? 99)
  );

  const paperCount = data.length;

  return (
    <div className="subjects">
      <header className="subjects-head">
        <h1>{isAdmin ? "All subjects" : "My subjects"}</h1>
        <p className="subjects-sub">
          {isAdmin ? (
            <>
              {paperCount} {paperCount === 1 ? "course" : "courses"} on the
              platform. Open one to read what learners are given.
            </>
          ) : (
            <>
              {paperCount} {paperCount === 1 ? "paper" : "papers"} on your list.
              Pick one to see its lessons, or{" "}
              <Link to="/browse">add another subject</Link>.
            </>
          )}
        </p>
      </header>

      {subjects.map((subject) => (
        <section key={subject} className="subject-set">
          <h2 className="subject-set-title">
            <span className="subject-set-mark" aria-hidden="true">
              {grouped[subject][0]?.icon}
            </span>
            {subject}
          </h2>

          <ul className="paper-list">
            {grouped[subject].map((c) => (
              <li key={c.id}>
                <Link to={`/learn/${c.id}`} className="paper">
                  <span className="paper-code">{paperCode(c.grade_label)}</span>

                  <span className="paper-body">
                    <span className="paper-title">{c.title}</span>
                    <span className="paper-desc">{c.description}</span>
                    <span className="paper-meta">
                      <span className="paper-figure">{c.lesson_count}</span>{" "}
                      {c.lesson_count === 1 ? "lesson" : "lessons"}
                      {c.publisher && (
                        <>
                          {" · "}
                          {/* Who wrote it. A learner choosing a course is
                              choosing whose course it is, so the byline is
                              part of the decision, not a footnote. */}
                          <span className="paper-byline">
                            Published by <strong>{c.publisher}</strong>
                          </span>
                        </>
                      )}
                    </span>
                  </span>

                  {/* The mark allocation, set in the margin exactly as it is on
                      a question paper. */}
                  <span className="paper-marks">
                    <span className="paper-marks-value">
                      {c.total_objectives}
                    </span>
                    <span className="paper-marks-label">objectives</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
